// Rules Store - SAT V4 Zone
import { create } from 'zustand';
import {
  mockActivities,
  mockHardRules,
  mockInitialReviewList,
  mockPreferences,
  mockProblemSolutions,
  mockSoftRules,
  mockTeacherPreferences,
  mockUnifiedRules,
} from '../lib/mockData';
import type {
  Activity,
  ApplyAsType,
  HardRule,
  Preference,
  PriorityType,
  ProblemSolution,
  ReviewListItem,
  SoftRule,
  UnifiedRule,
} from '../types';

interface RulesState {
  // Data
  hardRules: HardRule[];
  softRules: SoftRule[];
  preferences: Preference[];
  teacherPreferences: Preference[];
  activities: Activity[];
  problemSolutions: ProblemSolution[];

  // Unified Rules & Review List (new structure)
  unifiedRules: UnifiedRule[];
  reviewList: ReviewListItem[];

  // Actions - Hard Rules
  addHardRule: (rule: HardRule) => void;
  updateHardRule: (id: string, updates: Partial<HardRule>) => void;
  deleteHardRule: (id: string) => void;
  toggleHardRule: (id: string) => void;

  // Actions - Soft Rules
  addSoftRule: (rule: SoftRule) => void;
  updateSoftRule: (id: string, updates: Partial<SoftRule>) => void;
  deleteSoftRule: (id: string) => void;
  toggleSoftRule: (id: string) => void;

  // Actions - Preferences
  addPreference: (preference: Preference) => void;
  updatePreference: (id: string, updates: Partial<Preference>) => void;
  deletePreference: (id: string) => void;

  // Actions - Teacher Preferences
  addTeacherPreference: (preference: Preference) => void;
  updateTeacherPreference: (id: string, updates: Partial<Preference>) => void;
  deleteTeacherPreference: (id: string) => void;

  // Actions - Activities
  addActivity: (activity: Activity) => void;
  updateActivity: (id: string, updates: Partial<Activity>) => void;
  deleteActivity: (id: string) => void;

  // Actions - Problem Solutions
  addProblemSolution: (ps: ProblemSolution) => void;
  updateProblemSolution: (id: string, updates: Partial<ProblemSolution>) => void;
  deleteProblemSolution: (id: string) => void;

  // Actions - Unified Rules
  addCustomRule: (rule: UnifiedRule) => void;
  updateCustomRule: (id: string, updates: Partial<UnifiedRule>) => void;
  deleteCustomRule: (id: string) => void;

  // Actions - Review List
  addToReviewList: (ruleId: string, applyAs: ApplyAsType, priority?: PriorityType) => void;
  removeFromReviewList: (ruleId: string) => void;
  updateReviewItem: (ruleId: string, applyAs: ApplyAsType, priority?: PriorityType) => void;
  isInReviewList: (ruleId: string) => boolean;
  getReviewItem: (ruleId: string) => ReviewListItem | undefined;

  // Getters
  getActiveHardRules: () => HardRule[];
  getActiveSoftRules: () => SoftRule[];
  getPreferencesByPriority: (priority: 1 | 2 | 3) => Preference[];
  getRulesByType: (ruleType: 'SAT Rule' | 'Select Rule' | 'Customised Rule') => UnifiedRule[];
  getReviewListWithRules: () => (ReviewListItem & { rule: UnifiedRule })[];
}

export const useRulesStore = create<RulesState>((set, get) => ({
  // Initialize with mock data
  hardRules: mockHardRules,
  softRules: mockSoftRules,
  preferences: mockPreferences,
  teacherPreferences: mockTeacherPreferences,
  activities: mockActivities,
  problemSolutions: mockProblemSolutions,

  // Unified Rules & Review List
  unifiedRules: mockUnifiedRules,
  reviewList: mockInitialReviewList,

  // Hard Rules actions
  addHardRule: (rule) => set((state) => ({ hardRules: [...state.hardRules, rule] })),

  updateHardRule: (id, updates) =>
    set((state) => ({
      hardRules: state.hardRules.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    })),

  deleteHardRule: (id) =>
    set((state) => ({
      hardRules: state.hardRules.filter((r) => r.id !== id),
    })),

  toggleHardRule: (id) =>
    set((state) => ({
      hardRules: state.hardRules.map((r) => (r.id === id ? { ...r, isActive: !r.isActive } : r)),
    })),

  // Soft Rules actions
  addSoftRule: (rule) => set((state) => ({ softRules: [...state.softRules, rule] })),

  updateSoftRule: (id, updates) =>
    set((state) => ({
      softRules: state.softRules.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    })),

  deleteSoftRule: (id) =>
    set((state) => ({
      softRules: state.softRules.filter((r) => r.id !== id),
    })),

  toggleSoftRule: (id) =>
    set((state) => ({
      softRules: state.softRules.map((r) => (r.id === id ? { ...r, isActive: !r.isActive } : r)),
    })),

  // Preferences actions
  addPreference: (preference) => set((state) => ({ preferences: [...state.preferences, preference] })),

  updatePreference: (id, updates) =>
    set((state) => ({
      preferences: state.preferences.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),

  deletePreference: (id) =>
    set((state) => ({
      preferences: state.preferences.filter((p) => p.id !== id),
    })),

  // Teacher Preferences actions
  addTeacherPreference: (preference) =>
    set((state) => ({ teacherPreferences: [...state.teacherPreferences, preference] })),

  updateTeacherPreference: (id, updates) =>
    set((state) => ({
      teacherPreferences: state.teacherPreferences.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    })),

  deleteTeacherPreference: (id) =>
    set((state) => ({
      teacherPreferences: state.teacherPreferences.filter((p) => p.id !== id),
    })),

  // Activities actions
  addActivity: (activity) => set((state) => ({ activities: [...state.activities, activity] })),

  updateActivity: (id, updates) =>
    set((state) => ({
      activities: state.activities.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    })),

  deleteActivity: (id) =>
    set((state) => ({
      activities: state.activities.filter((a) => a.id !== id),
    })),

  // Problem Solutions actions
  addProblemSolution: (ps) => set((state) => ({ problemSolutions: [...state.problemSolutions, ps] })),

  updateProblemSolution: (id, updates) =>
    set((state) => ({
      problemSolutions: state.problemSolutions.map((ps) => (ps.id === id ? { ...ps, ...updates } : ps)),
    })),

  deleteProblemSolution: (id) =>
    set((state) => ({
      problemSolutions: state.problemSolutions.filter((ps) => ps.id !== id),
    })),

  // Unified Rules actions
  addCustomRule: (rule) => set((state) => ({ unifiedRules: [...state.unifiedRules, rule] })),

  updateCustomRule: (id, updates) =>
    set((state) => ({
      unifiedRules: state.unifiedRules.map((r) => (r.id === id ? { ...r, ...updates } : r)),
    })),

  deleteCustomRule: (id) =>
    set((state) => ({
      unifiedRules: state.unifiedRules.filter((r) => r.id !== id),
      // Also remove from review list if present
      reviewList: state.reviewList.filter((item) => item.ruleId !== id),
    })),

  // Review List actions
  addToReviewList: (ruleId, applyAs, priority) =>
    set((state) => ({
      reviewList: [...state.reviewList, { ruleId, applyAs, priority }],
    })),

  removeFromReviewList: (ruleId) =>
    set((state) => ({
      reviewList: state.reviewList.filter((item) => item.ruleId !== ruleId),
    })),

  updateReviewItem: (ruleId, applyAs, priority) =>
    set((state) => ({
      reviewList: state.reviewList.map((item) => (item.ruleId === ruleId ? { ...item, applyAs, priority } : item)),
    })),

  isInReviewList: (ruleId) => get().reviewList.some((item) => item.ruleId === ruleId),

  getReviewItem: (ruleId) => get().reviewList.find((item) => item.ruleId === ruleId),

  // Getters
  getActiveHardRules: () => get().hardRules.filter((r) => r.isActive),
  getActiveSoftRules: () => get().softRules.filter((r) => r.isActive),
  getPreferencesByPriority: (priority) => get().preferences.filter((p) => p.priority === priority),
  getRulesByType: (ruleType) => get().unifiedRules.filter((r) => r.ruleType === ruleType),
  getReviewListWithRules: () =>
    get()
      .reviewList.map((item) => {
        const rule = get().unifiedRules.find((r) => r.id === item.ruleId);
        return rule ? { ...item, rule } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null),
}));

export default useRulesStore;
